import { Button, Card, Tag, Tooltip, Typography } from "@hellocarbo/lasagne";
import { ArrowRight, LucideIcon } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { PortalContainerContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";

type OptionItemProps = {
  title: string;
  description: string;
  href: string;
  optionKey: string;
  tag?: string;
  Icon?: LucideIcon;
};

function OptionItem({
  title,
  description,
  href,
  tag,
  Icon,
  optionKey,
}: OptionItemProps) {
  const fullHref = `${href}?option=${encodeURIComponent(optionKey)}`;
  const { sendSegmentEvent } = useSegmentEvent();
  const { t } = useTranslation("options");
  const { container } = useContext(PortalContainerContext);

  return (
    <Card className="flex items-center justify-between">
      <div className="gap-x-sm flex items-center">
        {Icon && (
          <Icon
            data-testid="lucide-icon"
            className="size-lg text-grayscale-light"
          />
        )}
        <div>
          <Typography weight="bold">{title}</Typography>
          <Typography
            step="-1"
            weight="semibold"
            className="text-grayscale mt-3xs"
          >
            {description}
          </Typography>
        </div>
        {tag ? <Tag intent="primary" text={tag} /> : null}
        <Tooltip
          portalContainer={container as Element}
          side="top"
          content={t("options:tooltip")}
          triggerAsChild
        >
          <Button
            shape="soft"
            intent="secondary"
            as="a"
            href={fullHref}
            size="small"
            Icon={ArrowRight}
            onClick={() => sendSegmentEvent(`option_${optionKey}`)}
          />
        </Tooltip>
      </div>
    </Card>
  );
}

export default OptionItem;
